"use client";

import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { motion } from "motion/react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    EditorialTitle,
    GradientText,
    RevealOnScroll,
    AnimatedNumber,
    useIsDark,
} from "@/components/ui/Socials/JoinInstructors/_primitives";

const COMMISSION = 0.15; // 11% Believemy + 4% transaction
const WEEKS_PER_MONTH = 4.33;

export default function RevenueSimulator() {
    const t = useTranslations();
    const locale = useLocale();
    const isDark = useIsDark();
    const axisColor = isDark ? "rgba(255,255,255,0.4)" : "rgba(17,24,39,0.3)";
    const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(17,24,39,0.06)";
    const tooltipBg = isDark ? "#192734" : "#FFFFFF";
    const tooltipBorder = isDark ? "rgba(255,255,255,0.15)" : "rgba(17,24,39,0.15)";
    const tooltipLabel = isDark ? "rgba(255,255,255,0.7)" : "rgba(17,24,39,0.7)";

    const fmtNumber = (v) => Math.round(v).toLocaleString(locale);
    const fmtEuro = (v) => `${fmtNumber(v)} €`;
    const fmtEuroShort = (v) => {
        if (v >= 1000) {
            const k = (v / 1000).toLocaleString(locale, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
            });
            return `${k}k €`;
        }
        return `${Math.round(v)} €`;
    };
    const growthSuffix = t("joinMentor_sim_growth_suffix");

    const [price, setPrice] = useState(90);
    const [callsPerWeek, setCallsPerWeek] = useState(5);
    const [growth, setGrowth] = useState(4); // % growth/month

    const monthlyNet = useMemo(() => {
        const callsPerMonth = callsPerWeek * WEEKS_PER_MONTH;
        const gross = callsPerMonth * price;
        const net = gross * (1 - COMMISSION);
        return Math.round(net);
    }, [price, callsPerWeek]);

    const series = useMemo(() => {
        const out = [];
        const g = 1 + growth / 100;
        let calls = callsPerWeek * WEEKS_PER_MONTH;
        let cumul = 0;
        for (let m = 1; m <= 24; m++) {
            const monthRevenue = calls * price * (1 - COMMISSION);
            cumul += monthRevenue;
            out.push({
                month: `M${m}`,
                monthly: Math.round(monthRevenue),
                cumul: Math.round(cumul),
            });
            calls = calls * g;
        }
        return out;
    }, [price, callsPerWeek, growth]);

    const m12 = series[11];
    const m24 = series[23];
    const totalY1 = series.slice(0, 12).reduce((a, b) => a + b.monthly, 0);

    return (
        <section
            id="simulator"
            className="relative py-16 sm:py-20 lg:py-32 overflow-hidden bg-white dark:bg-[#0E161D]"
        >
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(232,81,29,0.10),transparent_60%)] dark:bg-[radial-gradient(ellipse_at_top,rgba(232,81,29,0.18),transparent_60%)]" />
            <div className="relative max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10">
                <div className="flex items-center gap-3 mb-8 sm:mb-10 text-[10px] uppercase tracking-[0.25em] text-gray-500 dark:text-white/55">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_8px_rgba(232,81,29,0.4)] animate-pulse" />
                    <span>{t("joinMentor_sim_marker")}</span>
                </div>

                <RevealOnScroll>
                    <p className="text-[11px] uppercase tracking-[0.28em] text-primary mb-5 sm:mb-6">
                        {t("joinMentor_sim_eyebrow")}
                    </p>
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-7xl mb-5 sm:mb-6">
                        {t("joinMentor_sim_title_part1")}{" "}
                        <GradientText>
                            {t("joinMentor_sim_title_emphasis")}
                        </GradientText>
                    </EditorialTitle>
                    <p
                        className="text-gray-500 dark:text-white/55 italic text-base sm:text-lg max-w-2xl mb-10 sm:mb-14"
                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                    >
                        {t("joinMentor_sim_subtitle")}
                    </p>
                </RevealOnScroll>

                <div className="grid lg:grid-cols-12 gap-5 sm:gap-6">
                    {/* Sliders */}
                    <RevealOnScroll className="lg:col-span-5">
                        <div className="rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0E161D] backdrop-blur p-6 sm:p-8">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-8 font-mono">
                                {t("joinMentor_sim_inputs_label")}
                            </div>

                            <Slider
                                isDark={isDark}
                                label={t("joinMentor_sim_price_label")}
                                hint={t("joinMentor_sim_price_hint")}
                                value={price}
                                onChange={setPrice}
                                min={20}
                                max={300}
                                step={5}
                                format={(v) => `${v} €`}
                            />

                            <Slider
                                isDark={isDark}
                                label={t("joinMentor_sim_calls_label")}
                                hint={t("joinMentor_sim_calls_hint")}
                                value={callsPerWeek}
                                onChange={setCallsPerWeek}
                                min={1}
                                max={30}
                                step={1}
                                format={(v) => fmtNumber(v)}
                            />

                            <Slider
                                isDark={isDark}
                                label={t("joinMentor_sim_growth_label")}
                                hint={t("joinMentor_sim_growth_hint")}
                                value={growth}
                                onChange={setGrowth}
                                min={0}
                                max={15}
                                step={1}
                                format={(v) => `+${v} %${growthSuffix}`}
                            />

                            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-white/10 grid grid-cols-2 gap-3 text-[10px] uppercase tracking-[0.25em] font-mono text-gray-400 dark:text-white/40">
                                <div className="flex justify-between">
                                    <span>{t("joinMentor_sim_assump_commission")}</span>
                                    <span className="text-primary">15 %</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>{t("joinMentor_sim_assump_payout")}</span>
                                    <span className="text-gray-900 dark:text-white">
                                        1–10 j
                                    </span>
                                </div>
                            </div>
                        </div>
                    </RevealOnScroll>

                    {/* Output */}
                    <div className="lg:col-span-7 space-y-5 sm:space-y-6">
                        <RevealOnScroll delay={0.05}>
                            <div className="rounded-2xl sm:rounded-3xl border border-primary/40 bg-gradient-to-br from-primary/10 via-transparent to-transparent p-6 sm:p-8">
                                <div className="flex flex-wrap items-end justify-between gap-5 sm:gap-6">
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-primary mb-3">
                                            {t("joinMentor_sim_main_kpi_label")}
                                        </div>
                                        <div
                                            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-none text-gray-900 dark:text-white break-words"
                                            style={{
                                                fontFamily:
                                                    "'Playfair Display', Georgia, serif",
                                            }}
                                        >
                                            <AnimatedNumber
                                                key={monthlyNet}
                                                value={monthlyNet}
                                                duration={0.8}
                                                format={fmtNumber}
                                            />
                                            <span className="text-2xl sm:text-3xl text-primary ml-2">
                                                €
                                            </span>
                                        </div>
                                        <p className="text-gray-500 dark:text-white/55 mt-3 text-xs sm:text-sm">
                                            {t("joinMentor_sim_main_kpi_desc")}
                                        </p>
                                    </div>
                                    <motion.div
                                        animate={{ scale: [1, 1.02, 1] }}
                                        transition={{
                                            duration: 2,
                                            repeat: Infinity,
                                        }}
                                        className="text-right"
                                    >
                                        <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-1">
                                            {t("joinMentor_sim_yearly_label")}
                                        </div>
                                        <div
                                            className="text-2xl sm:text-3xl text-gray-900 dark:text-white font-bold"
                                            style={{
                                                fontFamily:
                                                    "'Playfair Display', Georgia, serif",
                                            }}
                                        >
                                            {fmtEuro(totalY1)}
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </RevealOnScroll>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <KpiCard
                                label={t("joinMentor_sim_kpi_m12")}
                                value={fmtEuro(m12.monthly)}
                                sub={t("joinMentor_sim_kpi_m12_sub")}
                            />
                            <KpiCard
                                label={t("joinMentor_sim_kpi_y2")}
                                value={fmtEuro(m24.cumul)}
                                sub={t("joinMentor_sim_kpi_y2_sub")}
                                highlight
                            />
                        </div>

                        <RevealOnScroll delay={0.1}>
                            <div className="rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-4 sm:p-6">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40">
                                        {t("joinMentor_sim_chart_label")}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 font-mono">
                                        24 {t("joinMentor_sim_chart_months")}
                                    </div>
                                </div>
                                <div style={{ width: "100%", height: 240 }}>
                                    <ResponsiveContainer>
                                        <AreaChart
                                            data={series}
                                            margin={{
                                                top: 10,
                                                right: 10,
                                                left: -10,
                                                bottom: 0,
                                            }}
                                        >
                                            <defs>
                                                <linearGradient
                                                    id="sim-grad-m"
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1"
                                                >
                                                    <stop
                                                        offset="0%"
                                                        stopColor="#FF7A1A"
                                                        stopOpacity={0.8}
                                                    />
                                                    <stop
                                                        offset="100%"
                                                        stopColor="#E51578"
                                                        stopOpacity={0}
                                                    />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid
                                                stroke={gridColor}
                                                vertical={false}
                                            />
                                            <XAxis
                                                dataKey="month"
                                                stroke={axisColor}
                                                fontSize={10}
                                                tickLine={false}
                                                axisLine={false}
                                                interval={2}
                                            />
                                            <YAxis
                                                stroke={axisColor}
                                                fontSize={10}
                                                tickLine={false}
                                                axisLine={false}
                                                tickFormatter={fmtEuroShort}
                                            />
                                            <Tooltip
                                                contentStyle={{
                                                    background: tooltipBg,
                                                    border: `1px solid ${tooltipBorder}`,
                                                    borderRadius: 12,
                                                    fontSize: 12,
                                                }}
                                                labelStyle={{
                                                    color: tooltipLabel,
                                                }}
                                                formatter={(v) => [
                                                    fmtEuro(v),
                                                    t(
                                                        "joinMentor_sim_chart_legend_monthly",
                                                    ),
                                                ]}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="monthly"
                                                stroke="#E8511D"
                                                strokeWidth={2.5}
                                                fill="url(#sim-grad-m)"
                                                animationDuration={800}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </RevealOnScroll>
                    </div>
                </div>

                <RevealOnScroll delay={0.2}>
                    <p className="mt-10 text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 font-mono leading-relaxed text-center max-w-3xl mx-auto">
                        {t("joinMentor_sim_disclaimer")}
                    </p>
                </RevealOnScroll>
            </div>
        </section>
    );
}

function Slider({ isDark, label, hint, value, onChange, min, max, step, format }) {
    const trackColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(17,24,39,0.08)";
    const pct = ((value - min) / (max - min)) * 100;
    return (
        <div className="mb-7 last:mb-0">
            <div className="flex justify-between items-baseline mb-2">
                <label className="text-sm text-gray-700 dark:text-white/85">
                    {label}
                </label>
                <div
                    className="text-2xl font-bold text-primary"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                    {format(value)}
                </div>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="w-full appearance-none h-1.5 rounded-full accent-primary cursor-pointer"
                style={{
                    background: `linear-gradient(to right, #E8511D 0%, #E51578 ${pct}%, ${trackColor} ${pct}%, ${trackColor} 100%)`,
                }}
            />
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 dark:text-white/55 mt-2 font-mono">
                {hint}
            </p>
        </div>
    );
}

function KpiCard({ label, value, sub, highlight }) {
    return (
        <div
            className={`rounded-2xl p-5 ${
                highlight
                    ? "border border-primary/40 bg-primary/5"
                    : "border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03]"
            }`}
        >
            <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-2">
                {label}
            </div>
            <div
                className={`text-2xl md:text-3xl font-bold ${
                    highlight ? "text-primary" : "text-gray-900 dark:text-white"
                }`}
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
                {value}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-white/40 mt-2 font-mono">
                {sub}
            </p>
        </div>
    );
}
